import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { formatError } from '../utils/errorHandler';
import './FortnoxReferencePicker.css';

const FortnoxReferencePicker = ({
  type,
  value,
  onChange,
  disabled = false,
  placeholder
}) => {
  const { t } = useTranslation(['locations', 'common']);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);
  const wrapperRef = useRef(null);

  const endpoint = type === 'article'
    ? '/api/integrations/fortnox/articles'
    : '/api/integrations/fortnox/customers';

  const loadOptions = useCallback(async (term) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(endpoint, {
        params: { page: 1, limit: 20, search: term || undefined }
      });
      if (requestId !== requestIdRef.current) {
        return;
      }
      setOptions(response.data?.data?.items || []);
    } catch (err) {
      if (requestId !== requestIdRef.current) {
        return;
      }
      setError(formatError(err));
      setOptions([]);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [endpoint]);

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!open || disabled) {
      return;
    }
    loadOptions(searchDebounced);
  }, [open, searchDebounced, disabled, loadOptions]);

  useEffect(() => {
    if (!value || disabled || open) {
      return;
    }
    loadOptions(String(value));
  }, [value, disabled, open, loadOptions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getLabel = (item) => {
    if (type === 'article') {
      return `${item.number} — ${item.description || item.number}`;
    }
    return `${item.number} — ${item.name || item.number}`;
  };

  const selectedLabel = value
    ? options.find((item) => item.number === value)
      ? getLabel(options.find((item) => item.number === value))
      : value
    : '';

  return (
    <div className="fortnox-reference-picker" ref={wrapperRef}>
      <div className="fortnox-reference-picker-input-row">
        <input
          type="text"
          value={open ? search : selectedLabel}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!open) {
              setOpen(true);
            }
          }}
          onFocus={() => {
            setOpen(true);
            setSearch('');
          }}
          placeholder={placeholder || t('fortnoxPickerSearchPlaceholder')}
          disabled={disabled}
          aria-label={placeholder || t('fortnoxPickerSearchPlaceholder')}
        />
        {value && !disabled && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => onChange(null)}
          >
            {t('fortnoxPickerClear')}
          </button>
        )}
      </div>
      {open && !disabled && (
        <div className="fortnox-reference-picker-dropdown" role="listbox">
          {loading ? (
            <p className="fortnox-reference-picker-status">{t('fortnoxPickerLoading')}</p>
          ) : error ? (
            <p className="fortnox-reference-picker-status error">{error}</p>
          ) : options.length === 0 ? (
            <p className="fortnox-reference-picker-status">{t('fortnoxPickerNoResults')}</p>
          ) : (
            options.map((item) => (
              <button
                key={item.number}
                type="button"
                className={`fortnox-reference-picker-option${value === item.number ? ' selected' : ''}`}
                onClick={() => {
                  onChange(item.number);
                  setOpen(false);
                  setSearch('');
                }}
              >
                {getLabel(item)}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default FortnoxReferencePicker;
